import { captionLineBreakChanged } from '@src/editor/services/karaokeSegmentation';
import { useCallback, useMemo } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { CaptionSoftBreakAnchor } from '@src/editor/services/captionSoftBreaks';
import type { KaraokeLine } from '@src/editor/types/karaoke';

export interface UseKaraokeResult {
  // Query methods
  getKaraokeConfigs: () => Map<string, KaraokeConfig> | null;
  getKaraokeLines: () => KaraokeLine[];
  getDefaultKaraokeConfig: () => KaraokeConfig | null;
  getKaraokeConfigForKaraokeClipId: (
    karaokeClipId: string,
  ) => KaraokeConfig | null;

  // Config management handlers
  setDefaultKaraokeConfigId: (configId: string) => void;
  createKaraokeConfig: (config: Omit<KaraokeConfig, 'id'>) => KaraokeConfig;
  updateKaraokeConfigAndProduce: (config: KaraokeConfig) => void;

  commandDeleteKaraoke: (createUndoRedoEntry?: boolean) => boolean;

  // Command handlers from IKaraokeCommands
  commandProduceKaraoke: (options?: {
    replaceTrack: boolean;
    createUndoRedoEntry?: boolean;
  }) => boolean;
  commandApplyKaraokeConfig: (
    config: { id: string } | Omit<KaraokeConfig, 'id'>,
    karaokeClipIds?: string[],
  ) => boolean;
  commandAddBreak: (transcriptTokenId: string) => boolean;
  commandRemoveBreak: (transcriptTokenId: string) => boolean;
  commandToggleCaptionSoftBreak: (anchor: CaptionSoftBreakAnchor) => boolean;
  commandAdjustKaraokeClipTime: (
    karaokeClipId: string,
    offset: { startSeconds?: number; endSeconds?: number },
  ) => boolean;
  commandHideInKaraoke: (transcriptTokenIds: string[]) => boolean;
  commandShowInKaraoke: (transcriptTokenIds: string[]) => boolean;
}

/**
 * React hook for managing karaoke operations in the video editor
 * Provides action handlers to call karaoke commands and manage karaoke configuration
 *
 * @param editor - IVideoEditor instance
 * @returns Object with karaoke operation handlers
 */
export function useKaraoke(editor: IVideoEditor | null): UseKaraokeResult {
  // Query methods
  const getKaraokeConfigs = useCallback(() => {
    if (!editor) return null;
    return editor.getKaraokeConfigs();
  }, [editor]);

  const getKaraokeLines = useCallback(() => {
    if (!editor) return [];
    return editor.getKaraokeLines();
  }, [editor]);

  const getDefaultKaraokeConfig = useCallback(() => {
    if (!editor) return null;
    return editor.getDefaultKaraokeConfig();
  }, [editor]);

  const getKaraokeConfigForKaraokeClipId = useCallback(
    (karaokeClipId: string): KaraokeConfig | null => {
      if (!editor) return null;
      const karaokeClip =
        editor
          .getKaraokeTrack()
          ?.karaokeClips.find(element => element.id === karaokeClipId) || null;
      if (!karaokeClip) return null;
      return (
        editor.getKaraokeConfigs().get(karaokeClip.karaokeConfig.id) || null
      );
    },
    [editor],
  );

  // Config management handlers
  const setDefaultKaraokeConfigId = useCallback(
    (configId: string) => {
      if (!editor) return;
      editor.setDefaultKaraokeConfigId(configId);
    },
    [editor],
  );

  const createKaraokeConfig = useCallback(
    (config: Omit<KaraokeConfig, 'id'>): KaraokeConfig => {
      if (!editor) {
        throw new Error('Editor instance is not available');
      }
      return editor.createKaraokeConfig(config);
    },
    [editor],
  );

  const updateKaraokeConfigAndProduce = useCallback(
    (config: KaraokeConfig, createUndoRedoEntry: boolean = true) => {
      if (!editor) return;
      const existingConfig = editor.getKaraokeConfigs().get(config.id);
      if (!existingConfig) return;
      const lineBreakChanged = captionLineBreakChanged(existingConfig, config);
      // PLAT-9621: language change requires re-segmenting captions through
      // the phrase-boundary path (or back to the original character-length
      // path). Treat it like a line-break change.
      const languageChanged = config.language !== existingConfig.language;
      // A first produce (no caption lines yet) seeds the default template
      // through here; with nothing to re-style, it must build the track even
      // when the template's line break and language match the config.
      const hasNoCaptions = editor.getKaraokeLines().length === 0;
      const result = editor.commandUpdateKaraokeConfig(
        config,
        createUndoRedoEntry,
      );
      if (lineBreakChanged || languageChanged || hasNoCaptions) {
        editor.commandProduceKaraoke({
          replaceTrack: true,
          createUndoRedoEntry: false,
        });
      }
      return result;
    },
    [editor],
  );

  const commandDeleteKaraoke = useCallback(
    (createUndoRedoEntry: boolean = true) => {
      if (!editor) return false;
      const karaokeTrackId = editor.getKaraokeTrack()?.id;
      if (!karaokeTrackId) return false;
      return !!editor.commandRemoveTrack(karaokeTrackId, createUndoRedoEntry);
    },
    [editor],
  );

  // Command handlers from IKaraokeCommands
  const commandProduceKaraoke = useCallback(
    (options?: { replaceTrack: boolean; createUndoRedoEntry?: boolean }) => {
      if (!editor) return false;
      return editor.commandProduceKaraoke(options);
    },
    [editor],
  );

  const commandApplyKaraokeConfig = useCallback(
    (
      config: { id: string } | Omit<KaraokeConfig, 'id'>,
      karaokeClipIds?: string[],
    ) => {
      if (!editor) return false;
      return editor.commandApplyKaraokeConfig(config, karaokeClipIds);
    },
    [editor],
  );

  const commandAddBreak = useCallback(
    (transcriptTokenId: string) => {
      if (!editor) return false;
      return editor.commandAddBreak(transcriptTokenId);
    },
    [editor],
  );

  const commandRemoveBreak = useCallback(
    (transcriptTokenId: string) => {
      if (!editor) return false;
      return editor.commandRemoveBreak(transcriptTokenId);
    },
    [editor],
  );

  const commandToggleCaptionSoftBreak = useCallback(
    (anchor: CaptionSoftBreakAnchor) => {
      if (!editor) return false;
      return editor.commandToggleCaptionSoftBreak(anchor);
    },
    [editor],
  );

  const commandAdjustKaraokeClipTime = useCallback(
    (
      karaokeClipId: string,
      offset: { startSeconds?: number; endSeconds?: number },
    ) => {
      if (!editor) return false;
      return editor.commandAdjustKaraokeClipTime(karaokeClipId, offset);
    },
    [editor],
  );

  const commandHideInKaraoke = useCallback(
    (transcriptTokenIds: string[]) => {
      if (!editor) return false;
      return editor.commandHideInKaraoke(transcriptTokenIds);
    },
    [editor],
  );

  const commandShowInKaraoke = useCallback(
    (transcriptTokenIds: string[]) => {
      if (!editor) return false;
      return editor.commandShowInKaraoke(transcriptTokenIds);
    },
    [editor],
  );

  return useMemo(
    () => ({
      // Query methods
      getKaraokeConfigs,
      getKaraokeLines,
      getDefaultKaraokeConfig,
      getKaraokeConfigForKaraokeClipId,

      // Config management handlers
      setDefaultKaraokeConfigId,
      createKaraokeConfig,
      updateKaraokeConfigAndProduce,

      // Command handlers
      commandProduceKaraoke,
      commandDeleteKaraoke,
      commandApplyKaraokeConfig,
      commandAddBreak,
      commandRemoveBreak,
      commandToggleCaptionSoftBreak,
      commandAdjustKaraokeClipTime,
      commandHideInKaraoke,
      commandShowInKaraoke,
    }),
    [
      getKaraokeConfigs,
      getKaraokeLines,
      getDefaultKaraokeConfig,
      setDefaultKaraokeConfigId,
      createKaraokeConfig,
      commandProduceKaraoke,
      commandApplyKaraokeConfig,
      commandAddBreak,
      commandRemoveBreak,
      commandToggleCaptionSoftBreak,
      commandAdjustKaraokeClipTime,
      commandHideInKaraoke,
      commandShowInKaraoke,
    ],
  );
}
